import type { DateRange } from 'react-day-picker';
import { CalendarIcon, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { shortTzName, US_TIMEZONES, USER_TIMEZONE } from '../lib/timezone';

interface MeterInspectorToolbarProps {
  dateRange: { from: Date; to: Date };
  onDateRangeChange: (range: { from: Date; to: Date }) => void;
  timezoneOverride: string | null;
  onTimezoneChange: (tz: string | null) => void;
  meterTimezone: string | null;
}

/**
 * Shared controls for the meter inspector detail page.
 *
 * The date range drives all three data fetches (flow, events, alerts) and the
 * x-axis of both consumption charts, keeping them visually aligned.
 *
 * The timezone override shifts only server-side processed timestamps — event
 * `updatedAt` (and, in future, alert `createdAt`). Flow readings and alert
 * start/end windows describe the real world in meter-local time and are
 * always shown in meter time.
 */
export function MeterInspectorToolbar(
  {
    dateRange,
    onDateRangeChange,
    timezoneOverride,
    onTimezoneChange,
    meterTimezone,
  }: MeterInspectorToolbarProps,
) {
  return (
    <div className='flex flex-wrap items-start justify-end gap-3'>
      <div className='flex flex-col items-start gap-1'>
        <Select
          value={timezoneOverride ?? 'browser'}
          onValueChange={(v) => onTimezoneChange(v === 'browser' ? null : v)}
        >
          <SelectTrigger className='w-[220px] h-9 text-sm'>
            <Globe className='h-4 w-4 mr-2 shrink-0' />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='browser'>
              Browser ({shortTzName(USER_TIMEZONE)})
            </SelectItem>
            {US_TIMEZONES.map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
                {meterTimezone === tz.value ? ' (meter location)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-xs text-muted-foreground max-w-[220px]'>
          Event processed times only. Flow and alert windows always show meter time.
        </p>
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant='outline' size='sm' className='gap-2 h-9'>
            <CalendarIcon className='h-4 w-4' />
            Date range
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='end'>
          <Calendar
            mode='range'
            defaultMonth={dateRange.from}
            selected={{ from: dateRange.from, to: dateRange.to }}
            onSelect={(range: DateRange | undefined) => {
              if (range?.from && range?.to) {
                onDateRangeChange({ from: range.from, to: range.to });
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
